import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../atoms/Icon';
import SessionManager from '../../utils/sessionManager';
import CandidateChatWidget from '../CandidateChatWidget';

const CandidateDashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const session = SessionManager.getSession('candidate');
  const candidateName = session?.profile?.full_name || session?.profile?.first_name || 'User';
  const candidateEmail = session?.profile?.email || session?.email || '';
  const [currentBalance, setCurrentBalance] = useState(0);

  // Sidebar is expanded when hovered (desktop) or mobileOpen (mobile)
  const expanded = hovered;

  // Fetch current balance
  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const candidateId = session?.profile?.id || session?.user_id;
        if (!candidateId) return;
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api'}/credits/balance?candidate_id=${candidateId}`);
        const data = await response.json();
        setCurrentBalance(data.balance || 0);
      } catch (error) {
        console.error('Error fetching balance:', error);
      }
    };
    fetchBalance();
    const handleCreditUpdate = () => fetchBalance();
    window.addEventListener('creditBalanceUpdated', handleCreditUpdate);
    return () => window.removeEventListener('creditBalanceUpdated', handleCreditUpdate);
  }, [session]);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', path: '/candidate/dashboard' },
    { id: 'profile', label: 'My Profile', icon: 'person', path: '/candidate/profile' },
    { id: 'placement', label: 'Placement Training', icon: 'school', path: '/candidate/placement-training' },
    { id: 'applications', label: 'My Applications', icon: 'work', path: '/candidate/applications' },
    { id: 'courses', label: 'My Courses', icon: 'menu_book', path: '/candidate/courses' },
    { id: 'reports', label: 'My Reports', icon: 'assessment', path: '/candidate/reports' },
    { id: 'transactions', label: 'Transactions', icon: 'receipt_long', path: '/candidate/transactions' },
    { id: 'settings', label: 'Settings', icon: 'settings', path: '/candidate/settings' },
  ];

  const isActive = (path) => {
    if (path === '/candidate/placement-training') {
      return location.pathname.startsWith('/candidate/placement-training') ||
        location.pathname.startsWith('/placement-training');
    }
    return location.pathname === path;
  };

  const handleLogout = () => {
    SessionManager.clearSession('candidate');
    navigate('/login');
  };

  const handleNavClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ===== SIDEBAR ===== */}
      {/* Desktop: inline sidebar with hover expand */}
      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`
          hidden md:flex flex-col flex-shrink-0 bg-white border-r border-gray-200
          transition-all duration-300 ease-in-out overflow-hidden
          ${expanded ? 'w-64' : 'w-[70px]'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-gray-200 flex-shrink-0">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-orange-500 hover:text-orange-600 transition-colors overflow-hidden"
            title="Go to Home"
          >
            <span className="text-xl font-bold flex-shrink-0">A</span>
            <span className={`text-xl font-bold whitespace-nowrap transition-all duration-300 ${expanded ? 'opacity-100 w-auto' : 'opacity-0 w-0'}`}>
              ptirix
            </span>
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
          <ul className="space-y-1 px-2">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.path)}
                  title={!expanded ? item.label : undefined}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors whitespace-nowrap overflow-hidden ${
                    isActive(item.path)
                      ? 'bg-orange-50 text-orange-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon name={item.icon} size={20} className="flex-shrink-0" />
                  <span className={`text-sm font-medium transition-all duration-300 ${expanded ? 'opacity-100' : 'opacity-0 w-0'}`}>
                    {item.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User section */}
        <div className="border-t border-gray-200 p-3 flex-shrink-0 overflow-hidden">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
              {candidateName.charAt(0).toUpperCase()}
            </div>
            <div className={`min-w-0 transition-all duration-300 ${expanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}>
              <p className="text-sm font-semibold text-gray-900 truncate">{candidateName}</p>
              <p className="text-xs text-gray-500 truncate">{candidateEmail}</p>
            </div>
          </div>

          {expanded && (
            <div className="bg-gray-50 rounded-lg p-2 mb-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Credits</span>
                <span className="text-sm font-bold text-orange-600">{currentBalance}</span>
              </div>
            </div>
          )}

          <button
            onClick={handleLogout}
            title={!expanded ? 'Logout' : undefined}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap overflow-hidden"
          >
            <Icon name="logout" size={18} className="flex-shrink-0" />
            <span className={`text-sm font-medium transition-all duration-300 ${expanded ? 'opacity-100' : 'opacity-0 w-0'}`}>
              Logout
            </span>
          </button>
        </div>
      </aside>

      {/* Mobile: slide-over sidebar */}
      <aside
        className={`
          md:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col
          transition-transform duration-300 ease-in-out shadow-xl
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 flex-shrink-0">
          <button onClick={() => navigate('/')} className="text-xl font-bold text-orange-500 hover:text-orange-600 transition-colors">
            Aptirix
          </button>
          <button onClick={() => setMobileOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-600">
            <Icon name="close" size={20} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-orange-50 text-orange-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon name={item.icon} size={20} />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User */}
        <div className="border-t border-gray-200 p-4 flex-shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-semibold">
              {candidateName.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{candidateName}</p>
              <p className="text-xs text-gray-500 truncate">{candidateEmail}</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 mb-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Credits</span>
              <span className="text-sm font-bold text-orange-600">{currentBalance}</span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <Icon name="logout" size={18} />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* ===== MAIN CONTENT ===== */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top Header Bar */}
        <header className="h-16 bg-gradient-to-r from-teal-700 to-teal-800 flex items-center justify-between px-4 md:px-6 shadow-md flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(true)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white md:hidden"
            >
              <Icon name="menu" size={20} />
            </button>
            <input
              type="text"
              placeholder="Search..."
              className="w-40 sm:w-60 md:w-80 px-4 py-2 rounded-lg bg-white/90 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 text-sm"
            />
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <span className="text-white text-xs md:text-sm hidden sm:block">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
            <button className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white">
              <Icon name="notifications" size={20} />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-6">
          {children}
        </main>
      </div>

      {/* Floating Chat Widget */}
      <CandidateChatWidget />
    </div>
  );
};

export default CandidateDashboardLayout;
